import React from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	type ConnectorSource,
	type ThirdPartyConfigsBootstrap,
} from '../../../common/constants/schemas/3p-config';
import { type ProductKeys, type ProductKeys3P } from '../../constants/products';
import { OAuthBanner } from '../../ui/oauth-banner';
import { isProductSmartlink } from '../connectors';
import type { OAuthSource } from '../events/oauth-events';
import { serveQF3PBannerExperiment } from '../experiments';
import { PlacementSlot } from '../post-office-placement/placement-portal-context';
import { useShouldSuppressBannerForAnimation } from '../quick-find/use-suppress-banner-for-animation';

interface OAuthHandlersProps {
	product: ProductKeys;
	connectorSource: ConnectorSource;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	extraAttributes?: Record<string, unknown>;
}

interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

interface BannerProps {
	isOnSearchDialog?: boolean;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	/** Products eligible for display in the banner (pre-filtered and pre-ordered by the caller; component still checks userNeedsOAuth and outboundAuthUrl). */
	eligibleProducts: ProductKeys[];
	useOAuthHandlers: useOAuthHandlers;
	fireAnalyticsEvent: (payload: any) => void;
}

export const OAuthContainer = ({
	isOnSearchDialog,
	thirdPartyConfigs,
	eligibleProducts,
	useOAuthHandlers,
	fireAnalyticsEvent,
}: BannerProps): React.JSX.Element | null => {
	const suppressBanner = useShouldSuppressBannerForAnimation();

	if (fg('rovo_search_3p_banner_opt_out') || (isOnSearchDialog && suppressBanner)) {
		return null;
	}

	// First product that needs OAuth (eligibleProducts is already ordered + filtered at call site)
	const displayedProduct = eligibleProducts.find(
		(product) => thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth,
	);
	const displayedProduct3PConfig = displayedProduct
		? thirdPartyConfigs[displayedProduct as ProductKeys3P]
		: undefined;

	const outboundAuthUrl = displayedProduct3PConfig?.outboundAuthUrl;
	// a little redundant way of getting this value, but just making use of common function
	const isSmartlink = isProductSmartlink({ productKey: displayedProduct, thirdPartyConfigs });

	if (!displayedProduct || !outboundAuthUrl) {
		return null;
	}

	if (isOnSearchDialog && serveQF3PBannerExperiment()) {
		return <PlacementSlot trackImpressionOnMount={true} />;
	}

	const oauthBannerComponent = (
		<OAuthBanner
			product={displayedProduct}
			outboundAuthUrl={outboundAuthUrl}
			useOAuthHandlers={useOAuthHandlers}
			isSmartlink={isSmartlink}
			isOnSearchDialog={isOnSearchDialog}
			fireAnalyticsEvent={fireAnalyticsEvent}
		/>
	);

	return isOnSearchDialog ? (
		oauthBannerComponent
	) : (
		<FlagsProvider>{oauthBannerComponent}</FlagsProvider>
	);
};
